import { Controller } from "@hotwired/stimulus";

// Manages a stack of viewed job details with navigation
export default class extends Controller {
  static targets = [
    "stackContainer",
    "stackLayer1",
    "stackLayer2",
    "activeCard",
    "previous",
    "next",
    "counter",
  ];
  static values = {
    activeIndex: { type: Number, default: 0 },
  };

  connect() {
    console.log("Job stack controller connected");
    this.viewedJobs = []; // Array to store job IDs in order viewed
    this.isInitialized = false;

    // Initialize stack layers with default hidden state
    if (this.hasStackLayer1Target) {
      this.stackLayer1Target.style.opacity = "0";
      this.stackLayer1Target.style.transform = "translateY(-12px) scale(0.97)";
    }
    if (this.hasStackLayer2Target) {
      this.stackLayer2Target.style.opacity = "0";
      this.stackLayer2Target.style.transform = "translateY(-24px) scale(0.94)";
    }

    // Wait for next tick to ensure DOM is ready
    setTimeout(() => {
      this.isInitialized = true;
      this.updateNavigationButtons();
    }, 0);
  }

  // Called when a job is selected from the list
  addJob(event) {
    const jobId = event.detail.jobId;

    // If job is already in stack, navigate to it
    const existingIndex = this.viewedJobs.indexOf(jobId);
    if (existingIndex !== -1) {
      this.activeIndexValue = existingIndex;
      this.updateDisplay();
      return;
    }

    // Add new job to stack
    this.viewedJobs.push(jobId);
    this.activeIndexValue = this.viewedJobs.length - 1;

    this.updateDisplay();
  }

  // Navigate to previous job in stack
  previous(event) {
    event.preventDefault();

    if (this.activeIndexValue > 0) {
      this.activeIndexValue--;
      this.navigateToJob(this.viewedJobs[this.activeIndexValue]);
    }
  }

  // Navigate to next job in stack
  next(event) {
    event.preventDefault();

    if (this.activeIndexValue < this.viewedJobs.length - 1) {
      this.activeIndexValue++;
      this.navigateToJob(this.viewedJobs[this.activeIndexValue]);
    }
  }

  // Navigate to a specific job by triggering Turbo Frame load
  navigateToJob(jobId) {
    const frame = document.getElementById("job-detail");
    if (frame) {
      frame.src = `/talent/jobs/${jobId}`;
    }
    this.updateDisplay();
  }

  // Update the visual display of stacked cards
  updateDisplay() {
    if (!this.isInitialized) return;
    this.updateNavigationButtons();
    this.updateStackVisuals();
    this.updateCounter();
  }

  // Update navigation button states
  updateNavigationButtons() {
    if (this.hasPreviousTarget) {
      this.previousTarget.disabled = this.activeIndexValue <= 0;
      this.previousTarget.classList.toggle(
        "opacity-50",
        this.activeIndexValue <= 0
      );
      this.previousTarget.classList.toggle(
        "cursor-not-allowed",
        this.activeIndexValue <= 0
      );
    }

    if (this.hasNextTarget) {
      this.nextTarget.disabled =
        this.activeIndexValue >= this.viewedJobs.length - 1;
      this.nextTarget.classList.toggle(
        "opacity-50",
        this.activeIndexValue >= this.viewedJobs.length - 1
      );
      this.nextTarget.classList.toggle(
        "cursor-not-allowed",
        this.activeIndexValue >= this.viewedJobs.length - 1
      );
    }
  }

  // Update the counter display
  updateCounter() {
    if (this.hasCounterTarget && this.viewedJobs.length > 1) {
      this.counterTarget.textContent = `${this.activeIndexValue + 1} / ${
        this.viewedJobs.length
      }`;
      this.counterTarget.classList.remove("hidden");
    } else if (this.hasCounterTarget) {
      this.counterTarget.classList.add("hidden");
    }
  }

  // Update the visual stacking effect
  updateStackVisuals() {
    if (!this.hasStackLayer1Target || !this.hasStackLayer2Target) {
      return;
    }

    const hasPrevious = this.activeIndexValue > 0;

    // Get the height of the active card to match stack layer heights
    if (this.hasActiveCardTarget) {
      const activeCardHeight = this.activeCardTarget.offsetHeight;
      this.stackLayer1Target.style.height = `${activeCardHeight}px`;
      this.stackLayer2Target.style.height = `${activeCardHeight}px`;
    }

    // Show stack layers behind the current card to indicate browsing history
    // Layer 1 represents the most recent previous card
    if (hasPrevious) {
      this.stackLayer1Target.style.opacity = "0.8";
      this.stackLayer1Target.style.transform = "translateY(-12px) scale(0.97)";
    } else {
      this.stackLayer1Target.style.opacity = "0";
    }

    // Layer 2 represents the second most recent previous card
    if (this.activeIndexValue > 1) {
      this.stackLayer2Target.style.opacity = "0.6";
      this.stackLayer2Target.style.transform = "translateY(-24px) scale(0.94)";
    } else {
      this.stackLayer2Target.style.opacity = "0";
    }

    // Add subtle animation to active card
    if (this.hasActiveCardTarget) {
      this.activeCardTarget.style.transform = "scale(1)";
    }
  }

  // Handle when active index changes
  activeIndexValueChanged() {
    if (!this.isInitialized) return;
    this.updateDisplay();
  }

  // Reset the stack (useful for clearing history)
  reset() {
    this.viewedJobs = [];
    this.activeIndexValue = 0;
    this.updateDisplay();
  }

  // Get total number of jobs in stack
  get totalJobs() {
    return this.viewedJobs.length;
  }

  // Check if can go to previous
  get canGoPrevious() {
    return this.activeIndexValue > 0;
  }

  // Check if can go to next
  get canGoNext() {
    return this.activeIndexValue < this.viewedJobs.length - 1;
  }
}
